
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { useCallback, useRef } from 'react';

export type AnalyticsAction = 
  | 'modal_opened' 
  | 'modal_minimized' 
  | 'modal_closed'
  | 'prayer_requested' 
  | 'prayer_generated' 
  | 'prayer_copied' 
  | 'settings_changed';

export function useAiPrayerAnalytics() {
  const { user } = useAuth();
  const debounceMap = useRef(new Map<string, NodeJS.Timeout>());
  const recentActions = useRef(new Set<string>());

  const trackAction = useCallback(async (action: AnalyticsAction, metadata?: Record<string, any>) => {
    // Create a unique key for deduplication
    const actionKey = `${action}-${JSON.stringify(metadata || {})}`;
    
    // Skip if we've seen this exact action recently (within 1 second)
    if (recentActions.current.has(actionKey)) {
      return;
    }
    
    // Add to recent actions and expire after 1 second
    recentActions.current.add(actionKey);
    setTimeout(() => recentActions.current.delete(actionKey), 1000);
    
    // Clear existing debounce for this action type
    const existingTimeout = debounceMap.current.get(action);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Debounce the actual tracking call
    const timeoutId = setTimeout(async () => {
      if (!user?.id) {
        // Reduced logging for unauthenticated users
        return;
      }

      try {
        const { error } = await supabase
          .from('ai_prayer_analytics')
          .insert({
            user_id: user.id,
            action_type: action,
            metadata: metadata || {}
          });

        if (error) {
          // Reduce log verbosity - only warn, don't log fallback
          console.warn('Analytics error:', error.message);
        }
      } catch (error) {
        // Reduce error logging
        console.warn('Analytics failed:', (error as Error).message);
      }
      
      debounceMap.current.delete(action);
    }, 300); // 300ms debounce
    
    debounceMap.current.set(action, timeoutId);
  }, [user?.id]);

  return { trackAction };
}
